import { Button, Form } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BmoModal } from '@/components/BmoModal';

import { StepDetailsFormFields } from '../../StepDetailsFormFields';

type Props = {
  open: boolean;
  initial: {
    titleEn: string;
    titleFr: string;
    navTitleEn: string;
    navTitleFr: string;
    descriptionEn: string;
    descriptionFr: string;
  };
  onCancel: () => void;
  onSubmit: (values: {
    titleEn: string;
    titleFr: string;
    navTitleEn: string;
    navTitleFr: string;
    descriptionEn: string;
    descriptionFr: string;
  }) => void;
};

/**
 * Name/description editor for a Step custom element — the button beside Preview in
 * the Custom Editor's Step mode. Reuses the same fields as the live workflow's
 * {@link EditStepModal} via {@link StepDetailsFormFields}; submits back into the
 * Custom Editor's local state rather than a `workflowsApi` PATCH (no network here —
 * the editor's own explicit Save persists everything at once).
 */
export function CustomStepDetailsModal({ open, initial, onCancel, onSubmit }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) form.setFieldsValue(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOk = async () => {
    const values = await form.validateFields();
    onSubmit({
      titleEn: values.titleEn,
      titleFr: values.titleFr ?? '',
      navTitleEn: values.navTitleEn ?? '',
      navTitleFr: values.navTitleFr ?? '',
      descriptionEn: values.descriptionEn ?? '',
      descriptionFr: values.descriptionFr ?? '',
    });
  };

  return (
    <BmoModal
      open={open}
      onCancel={onCancel}
      title={t('workflow.customElements.stepDetailsModalTitle')}
      width={560}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common.cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={() => void handleOk()}>
          {t('common.save')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <StepDetailsFormFields />
      </Form>
    </BmoModal>
  );
}
